import React, { useState, useRef, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Check, Play, FileText, MessageSquare, ArrowLeft, ChevronRight,
  Award, ChevronDown, Lock, Volume2, VolumeX, Maximize, RotateCcw,
  Trophy, X, ArrowRight, BookOpen, Pause, SkipForward
} from "lucide-react";
import { courses, getCurriculum, getQuiz } from "../data/mockData";
import Topbar from "../components/Topbar";

// ── Quiz Panel (inline, no page change) ──────────────────────────────────────
function QuizPanel({ quiz, onClose }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [done, setDone] = useState(false);

  const q = quiz.questions[step];
  const total = quiz.questions.length;
  const score = quiz.questions.reduce(
    (acc, qq) => acc + (answers[qq.id] === qq.correctIndex ? 1 : 0), 0
  );
  const percent = Math.round((score / total) * 100);
  const passed = percent >= 60;

  const submit = () => { if (answers[q.id] !== undefined) setRevealed(true); };
  const next = () => {
    setRevealed(false);
    if (step < total - 1) setStep(step + 1);
    else setDone(true);
  };
  const restart = () => { setStep(0); setAnswers({}); setRevealed(false); setDone(false); };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 50,
      background: "rgba(14,15,18,0.97)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "24px"
    }}>
      {/* Close */}
      <button onClick={onClose} style={{
        position: "absolute", top: 20, right: 20,
        background: "rgba(255,255,255,0.08)", border: "none",
        borderRadius: "50%", width: 40, height: 40,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", color: "#fff"
      }}>
        <X size={18} />
      </button>

      <div style={{ width: "100%", maxWidth: 680 }}>
        {!done ? (
          <>
            {/* Header */}
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#7C5CFF", fontWeight: 700 }}>
                  {quiz.title}
                </span>
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  <span style={{ color: "#fff", fontWeight: 700 }}>{step + 1}</span> / {total}
                </span>
              </div>
              {/* Progress bar */}
              <div style={{ height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 99, overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 99,
                  background: "linear-gradient(90deg, #7C5CFF, #CFFF50)",
                  width: `${((step + 1) / total) * 100}%`,
                  transition: "width 0.4s ease"
                }} />
              </div>
            </div>

            {/* Question */}
            <h2 style={{
              fontSize: "clamp(20px, 4vw, 32px)", fontWeight: 900,
              lineHeight: 1.2, letterSpacing: "-0.02em",
              marginBottom: 32, color: "#fff"
            }}>{q.text}</h2>

            {/* Options */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 32 }}>
              {q.options.map((opt, idx) => {
                const selected = answers[q.id] === idx;
                const isCorrect = idx === q.correctIndex;
                let borderColor = "rgba(255,255,255,0.1)";
                let bg = "rgba(255,255,255,0.03)";
                if (revealed) {
                  if (isCorrect) { borderColor = "#CFFF50"; bg = "rgba(207,255,80,0.08)"; }
                  else if (selected) { borderColor = "#ef4444"; bg = "rgba(239,68,68,0.08)"; }
                  else { bg = "transparent"; borderColor = "rgba(255,255,255,0.04)"; }
                } else if (selected) {
                  borderColor = "#7C5CFF"; bg = "rgba(124,92,255,0.1)";
                }
                return (
                  <button
                    key={idx}
                    disabled={revealed}
                    onClick={() => setAnswers({ ...answers, [q.id]: idx })}
                    data-testid={`option-${idx}`}
                    style={{
                      width: "100%", textAlign: "left",
                      padding: "18px 24px", borderRadius: 16,
                      border: `2px solid ${borderColor}`,
                      background: bg,
                      cursor: revealed ? "default" : "pointer",
                      display: "flex", alignItems: "center",
                      justifyContent: "space-between", gap: 16,
                      transition: "all 0.2s ease", color: "#fff",
                      opacity: revealed && !isCorrect && !selected ? 0.4 : 1
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <span style={{
                        width: 32, height: 32, borderRadius: "50%",
                        border: `2px solid ${selected || (revealed && isCorrect) ? "#CFFF50" : "rgba(255,255,255,0.2)"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, fontWeight: 700, flexShrink: 0,
                        color: selected || (revealed && isCorrect) ? "#CFFF50" : "#6b7280"
                      }}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 500 }}>{opt}</span>
                    </span>
                    {revealed && isCorrect && <Check size={18} color="#CFFF50" />}
                    {revealed && selected && !isCorrect && <X size={18} color="#ef4444" />}
                  </button>
                );
              })}
            </div>

            {/* Action */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {!revealed ? (
                <button
                  onClick={submit}
                  disabled={answers[q.id] === undefined}
                  data-testid="submit-answer"
                  style={{
                    padding: "14px 32px", borderRadius: 99,
                    background: answers[q.id] === undefined ? "rgba(207,255,80,0.3)" : "#CFFF50",
                    color: "#0E0F12", fontWeight: 800, fontSize: 14,
                    border: "none", cursor: answers[q.id] === undefined ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", gap: 8,
                    transition: "all 0.2s"
                  }}
                >
                  Submit answer <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  onClick={next}
                  data-testid="next-question"
                  style={{
                    padding: "14px 32px", borderRadius: 99,
                    background: "#CFFF50", color: "#0E0F12",
                    fontWeight: 800, fontSize: 14, border: "none",
                    cursor: "pointer", display: "flex", alignItems: "center", gap: 8
                  }}
                >
                  {step < total - 1 ? "Next question" : "See results"} <ArrowRight size={16} />
                </button>
              )}
            </div>
          </>
        ) : (
          /* Results */
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 96, height: 96, borderRadius: "50%", margin: "0 auto 24px",
              background: passed ? "rgba(207,255,80,0.12)" : "rgba(239,68,68,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <Trophy size={40} color={passed ? "#CFFF50" : "#ef4444"} />
            </div>
            <h2 style={{ fontSize: "clamp(32px,6vw,56px)", fontWeight: 900, letterSpacing: "-0.04em", marginBottom: 12 }}>
              {passed ? "Nice work." : "Keep going."}
            </h2>
            <p style={{ color: "#6b7280", fontSize: 15, marginBottom: 40 }}>
              {passed ? "You passed. Certificate progress updated." : "Score 60% or above to pass. You've got this."}
            </p>

            <div style={{
              display: "inline-flex", gap: 48, padding: "28px 48px",
              borderRadius: 24, border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(255,255,255,0.03)", marginBottom: 40
            }}>
              {[
                { label: "Score", value: `${score}/${total}` },
                { label: "Percent", value: `${percent}%`, color: passed ? "#CFFF50" : "#ef4444" },
                { label: "Status", value: passed ? "Pass" : "Retry", color: passed ? "#CFFF50" : "#ef4444" }
              ].map((s) => (
                <div key={s.label}>
                  <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#4b5563", marginBottom: 6 }}>{s.label}</div>
                  <div style={{ fontSize: 36, fontWeight: 900, color: s.color || "#fff" }}>{s.value}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
              <button
                onClick={restart}
                data-testid="retake-quiz"
                style={{
                  padding: "14px 28px", borderRadius: 99,
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: "transparent", color: "#fff",
                  fontWeight: 600, fontSize: 14, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 8
                }}
              >
                <RotateCcw size={15} /> Retake
              </button>
              <button
                onClick={onClose}
                data-testid="continue-course"
                style={{
                  padding: "14px 28px", borderRadius: 99,
                  background: "#CFFF50", color: "#0E0F12",
                  fontWeight: 800, fontSize: 14, border: "none",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 8
                }}
              >
                Continue course <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Course Player ────────────────────────────────────────────────────────
export default function CoursePlayer() {
  const { courseId } = useParams();
  const course = courses.find((c) => c.id === courseId) || courses[0];
  const curriculum = getCurriculum(course.id);
  const quiz = getQuiz(course.id);

  const flatLessons = curriculum.flatMap((s) =>
    s.lessons.map((l) => ({ ...l, stage: s.stage }))
  );
  const firstIncomplete = flatLessons.findIndex((l) => !l.completed);
  const [activeIdx, setActiveIdx] = useState(firstIncomplete >= 0 ? firstIncomplete : 0);
  const [completedMap, setCompletedMap] = useState(
    Object.fromEntries(flatLessons.map((l) => [l.id, l.completed]))
  );
  const [tab, setTab] = useState("overview");
  const [showQuiz, setShowQuiz] = useState(false);
  const [expandedStages, setExpandedStages] = useState(
    Object.fromEntries(curriculum.map((s) => [s.stage, true]))
  );
  const [notes, setNotes] = useState("");
  const [muted, setMuted] = useState(false);
  const videoRef = useRef(null);

  const lesson = flatLessons[activeIdx];
  const completedCount = Object.values(completedMap).filter(Boolean).length;
  const percent = Math.round((completedCount / flatLessons.length) * 100);
  const isLastLesson = activeIdx === flatLessons.length - 1;

  const markComplete = () => {
    setCompletedMap((m) => ({ ...m, [lesson.id]: true }));
    if (!isLastLesson) setActiveIdx(activeIdx + 1);
  };

  const toggleStage = (stage) =>
    setExpandedStages((e) => ({ ...e, [stage]: !e[stage] }));

  // Reset video on lesson change
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
    setTab("overview");
  }, [activeIdx]);

  const S = {
    // colour tokens
    bg: "#0A0B0E",
    surface: "#111318",
    surfaceHover: "#161920",
    border: "rgba(255,255,255,0.07)",
    lime: "#CFFF50",
    grape: "#7C5CFF",
    textMuted: "#6b7280",
    textSub: "#9ca3af",
  };

  return (
    <div style={{ minHeight: "100vh", background: S.bg, color: "#fff", fontFamily: "inherit" }}>
      <Topbar searchPlaceholder="Search lessons..." />

      {/* Quiz overlay */}
      {showQuiz && <QuizPanel quiz={quiz} onClose={() => setShowQuiz(false)} />}

      {/* Top bar */}
      <div style={{
        padding: "12px 24px", borderBottom: `1px solid ${S.border}`,
        display: "flex", alignItems: "center", gap: 16,
        background: S.surface
      }}>
        <Link
          to="/my-courses"
          data-testid="back-to-courses"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 13, color: S.textMuted, textDecoration: "none",
            transition: "color 0.2s"
          }}
        >
          <ArrowLeft size={15} /> My courses
        </Link>
        <span style={{ color: S.border, fontSize: 18 }}>›</span>
        <span style={{ fontSize: 13, color: S.textSub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {course.title}
        </span>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16 }}>
          {/* Progress pill */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 80, height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 99, overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 99,
                background: `linear-gradient(90deg, ${S.grape}, ${S.lime})`,
                width: `${percent}%`, transition: "width 0.5s ease"
              }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: S.lime }}>{percent}%</span>
          </div>

          <button
            onClick={() => setShowQuiz(true)}
            data-testid="take-quiz-btn"
            style={{
              padding: "8px 18px", borderRadius: 99,
              background: S.grape, color: "#fff",
              fontWeight: 700, fontSize: 13, border: "none",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6
            }}
          >
            <BookOpen size={14} /> Take quiz
          </button>
        </div>
      </div>

      {/* Main layout */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", height: "calc(100vh - 113px)" }}>

        {/* ── Left: video + tabs ── */}
        <div style={{ overflowY: "auto", padding: "24px 28px" }}>

          {/* Video */}
          <div style={{
            borderRadius: 20, overflow: "hidden",
            background: "#000", position: "relative",
            aspectRatio: "16/9", marginBottom: 24,
            border: `1px solid ${S.border}`,
            boxShadow: "0 32px 64px rgba(0,0,0,0.5)"
          }}>
            <video
              ref={videoRef}
              key={lesson.id}
              data-testid="lesson-video"
              src={lesson.video}
              poster={course.cover}
              controls
              muted={muted}
              style={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }}
            >
              Your browser does not support video.
            </video>
          </div>

          {/* Lesson info */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
              color: S.grape, fontWeight: 700, marginBottom: 8
            }}>
              {lesson.stage}
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div>
                <h1 style={{
                  fontSize: "clamp(20px,3vw,28px)", fontWeight: 900,
                  letterSpacing: "-0.025em", lineHeight: 1.2, marginBottom: 6
                }}>{lesson.title}</h1>
                <p style={{ fontSize: 13, color: S.textMuted }}>
                  {course.title} · {course.instructor?.name || "BMG Instructor"} · {lesson.duration}
                </p>
              </div>
              <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                {!isLastLesson ? (
                  <button
                    onClick={markComplete}
                    data-testid="mark-complete-btn"
                    style={{
                      padding: "10px 22px", borderRadius: 99,
                      background: completedMap[lesson.id] ? "rgba(207,255,80,0.15)" : S.lime,
                      color: completedMap[lesson.id] ? S.lime : "#0A0B0E",
                      fontWeight: 800, fontSize: 13, border: completedMap[lesson.id] ? `1px solid ${S.lime}` : "none",
                      cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                      transition: "all 0.2s"
                    }}
                  >
                    {completedMap[lesson.id]
                      ? <><Check size={14} /> Completed</>
                      : <><Check size={14} /> Mark complete & next</>
                    }
                  </button>
                ) : (
                  <button
                    onClick={() => setShowQuiz(true)}
                    style={{
                      padding: "10px 22px", borderRadius: 99,
                      background: S.lime, color: "#0A0B0E",
                      fontWeight: 800, fontSize: 13, border: "none",
                      cursor: "pointer", display: "flex", alignItems: "center", gap: 6
                    }}
                  >
                    <Trophy size={14} /> Complete & take quiz
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{
            borderRadius: 16, border: `1px solid ${S.border}`,
            background: S.surface, overflow: "hidden"
          }}>
            {/* Tab bar */}
            <div style={{
              display: "flex", borderBottom: `1px solid ${S.border}`
            }}>
              {[
                { key: "overview", label: "Overview", icon: Play },
                { key: "notes", label: "Notes", icon: FileText },
                { key: "transcript", label: "Transcript", icon: MessageSquare },
              ].map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`tab-${t.key}`}
                    style={{
                      flex: 1, padding: "14px 16px",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      fontSize: 13, fontWeight: active ? 700 : 500,
                      color: active ? "#fff" : S.textMuted,
                      background: "transparent", border: "none",
                      cursor: "pointer", position: "relative",
                      transition: "color 0.2s"
                    }}
                  >
                    <Icon size={14} />
                    {t.label}
                    {active && (
                      <span style={{
                        position: "absolute", bottom: 0,
                        left: "50%", transform: "translateX(-50%)",
                        width: 40, height: 2,
                        background: S.lime, borderRadius: 99
                      }} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <div style={{ padding: 24, minHeight: 160, fontSize: 14, color: S.textSub, lineHeight: 1.7 }}>
              {tab === "overview" && (
                <div>
                  <p style={{ marginBottom: 12 }}>
                    In this lesson you'll learn the core principles of <strong style={{ color: "#fff" }}>{lesson.title}</strong>, see practical demonstrations from experienced BMG operators, and apply them to a hands-on exercise.
                  </p>
                  <p>By the end of this lesson you will be able to identify common mistakes, set up a clean workflow, and explain the technique to others in your team.</p>
                </div>
              )}
              {tab === "notes" && (
                <textarea
                  data-testid="notes-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Take notes while you learn..."
                  style={{
                    width: "100%", minHeight: 160,
                    background: "rgba(255,255,255,0.03)",
                    border: `1px solid ${S.border}`,
                    borderRadius: 12, padding: 16,
                    color: "#fff", fontSize: 14, lineHeight: 1.6,
                    resize: "vertical", outline: "none",
                    fontFamily: "inherit", boxSizing: "border-box",
                    transition: "border-color 0.2s"
                  }}
                  onFocus={(e) => e.target.style.borderColor = "rgba(207,255,80,0.4)"}
                  onBlur={(e) => e.target.style.borderColor = S.border}
                />
              )}
              {tab === "transcript" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {[
                    { time: "00:00", text: `Welcome. In today's lesson we'll be covering ${lesson.title.toLowerCase()}.` },
                    { time: "00:42", text: "Let's start with the fundamentals — what makes this approach so effective in the field." },
                    { time: "02:10", text: "Notice the technique here. That's the result of years of operational experience distilled into a single workflow." },
                    { time: "04:55", text: "Now let's walk through this together with a live scenario." },
                    { time: "07:30", text: "Before we close, a quick summary of what we've covered and how it applies to your next deployment." },
                  ].map((line) => (
                    <div key={line.time} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                      <span style={{
                        fontFamily: "monospace", fontSize: 11,
                        color: S.grape, fontWeight: 700, flexShrink: 0,
                        marginTop: 2, background: "rgba(124,92,255,0.12)",
                        padding: "2px 6px", borderRadius: 4
                      }}>{line.time}</span>
                      <span>{line.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quiz CTA at bottom */}
          <div style={{
            marginTop: 24, padding: "20px 24px",
            borderRadius: 16, border: `1px solid rgba(124,92,255,0.25)`,
            background: "rgba(124,92,255,0.07)",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16
          }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>Ready to test your knowledge?</div>
              <div style={{ fontSize: 13, color: S.textMuted }}>{quiz.questions?.length || 0} questions · Pass mark 60% · Certificate on completion</div>
            </div>
            <button
              onClick={() => setShowQuiz(true)}
              style={{
                padding: "12px 24px", borderRadius: 99,
                background: S.grape, color: "#fff",
                fontWeight: 800, fontSize: 13, border: "none",
                cursor: "pointer", whiteSpace: "nowrap",
                display: "flex", alignItems: "center", gap: 6
              }}
            >
              <BookOpen size={14} /> Start quiz
            </button>
          </div>
        </div>

        {/* ── Right: lesson sidebar ── */}
        <aside style={{
          borderLeft: `1px solid ${S.border}`,
          background: S.surface,
          overflowY: "auto",
          display: "flex", flexDirection: "column"
        }}>
          {/* Header */}
          <div style={{ padding: "18px 20px", borderBottom: `1px solid ${S.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: S.textMuted, marginBottom: 10 }}>
              Course contents
            </div>
            {/* Progress */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: S.textMuted }}>{completedCount} / {flatLessons.length} lessons</span>
              <span style={{ fontSize: 12, fontWeight: 800, color: S.lime }}>{percent}%</span>
            </div>
            <div style={{ height: 4, background: "rgba(255,255,255,0.07)", borderRadius: 99, overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 99,
                background: `linear-gradient(90deg, ${S.grape}, ${S.lime})`,
                width: `${percent}%`, transition: "width 0.5s ease"
              }} />
            </div>
          </div>

          {/* Curriculum */}
          <div style={{ flex: 1, overflowY: "auto", padding: "12px 0" }}>
            {curriculum.map((stage, sIdx) => (
              <div key={sIdx} style={{ marginBottom: 4 }}>
                {/* Stage header */}
                <button
                  onClick={() => toggleStage(stage.stage)}
                  style={{
                    width: "100%", padding: "10px 20px",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: "transparent", border: "none",
                    cursor: "pointer", color: S.textMuted
                  }}
                >
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                    {stage.stage}
                  </span>
                  <ChevronDown
                    size={14}
                    style={{ transition: "transform 0.2s", transform: expandedStages[stage.stage] ? "rotate(180deg)" : "rotate(0deg)" }}
                  />
                </button>

                {/* Lessons */}
                {expandedStages[stage.stage] && (
                  <div>
                    {stage.lessons.map((l) => {
                      const idx = flatLessons.findIndex((fl) => fl.id === l.id);
                      const isActive = idx === activeIdx;
                      const isComplete = completedMap[l.id];
                      return (
                        <button
                          key={l.id}
                          onClick={() => setActiveIdx(idx)}
                          data-testid={`lesson-${l.id}`}
                          style={{
                            width: "100%", padding: "10px 20px",
                            display: "flex", alignItems: "center", gap: 12,
                            background: isActive ? "rgba(207,255,80,0.06)" : "transparent",
                            borderLeft: isActive ? `3px solid ${S.lime}` : "3px solid transparent",
                            border: "none", cursor: "pointer",
                            textAlign: "left", transition: "all 0.15s"
                          }}
                        >
                          {/* Status dot */}
                          <span style={{
                            width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: isComplete ? S.grape : isActive ? S.lime : "rgba(255,255,255,0.06)",
                            border: isComplete || isActive ? "none" : `1px solid rgba(255,255,255,0.12)`
                          }}>
                            {isComplete
                              ? <Check size={11} color="#fff" />
                              : isActive
                                ? <Play size={10} color="#0A0B0E" fill="#0A0B0E" />
                                : null
                            }
                          </span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 13, fontWeight: isActive ? 700 : 400,
                              color: isActive ? "#fff" : S.textSub,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              marginBottom: 2
                            }}>{l.title}</div>
                            <div style={{ fontSize: 11, color: S.textMuted }}>{l.duration}</div>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Certificate CTA */}
          {percent === 100 && (
            <div style={{ padding: "16px 20px", borderTop: `1px solid ${S.border}` }}>
              <Link
                to="/certificates"
                data-testid="claim-cert-btn"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  gap: 8, padding: "14px 20px", borderRadius: 12,
                  background: S.lime, color: "#0A0B0E",
                  fontWeight: 800, fontSize: 14, textDecoration: "none"
                }}
              >
                <Award size={16} /> Claim your certificate
              </Link>
            </div>
          )}

          {/* Quiz shortcut */}
          <div style={{ padding: "16px 20px", borderTop: `1px solid ${S.border}` }}>
            <button
              onClick={() => setShowQuiz(true)}
              style={{
                width: "100%", padding: "12px 16px", borderRadius: 12,
                border: `1px solid rgba(124,92,255,0.3)`,
                background: "rgba(124,92,255,0.08)", color: "#fff",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6
              }}
            >
              <BookOpen size={14} color={S.grape} /> Take the quiz
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
